import React, {useState} from 'react';
import {Button, Card, Input, Switch, Modal, Form, Select, message, Popconfirm, Spin} from 'antd';
import {collection, getDocs, doc, setDoc, deleteDoc, updateDoc} from "firebase/firestore";
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {db} from '../base';
import NavBar from "../components/NavBar";

const RESERVED_IDS = ['0', '4'];
const ALL_DIGITS = ['1', '2', '3', '5', '6', '7', '8', '9'];

const fetchObiettivi = async () => {
    const snapshot = await getDocs(collection(db, 'obiettivi'));
    return snapshot.docs.map(d => ({id: d.id, ...d.data()}));
};

function ObiettiviPage() {
    const queryClient = useQueryClient();
    const [addOpen, setAddOpen] = useState(false);
    const [form] = Form.useForm();

    const {data: obiettivi = [], isLoading} = useQuery({
        queryKey: ['obiettivi'],
        queryFn: fetchObiettivi,
    });

    const usedIds = new Set(obiettivi.map(o => o.id));
    const availableIds = ALL_DIGITS.filter(d => !usedIds.has(d));

    const sorted = [...obiettivi].sort((a, b) => (b.punti ?? 0) - (a.punti ?? 0));

    async function handleSave(id, patch) {
        try {
            await updateDoc(doc(db, 'obiettivi', id), patch);
            await queryClient.invalidateQueries({queryKey: ['obiettivi']});
            message.success('Salvato');
        } catch (e) {
            message.error('Errore nel salvataggio');
        }
    }

    async function handleDelete(id) {
        try {
            await deleteDoc(doc(db, 'obiettivi', id));
            await queryClient.invalidateQueries({queryKey: ['obiettivi']});
            message.success('Eliminato');
        } catch (e) {
            message.error('Errore nell\'eliminazione');
        }
    }

    async function handleAdd(values) {
        try {
            await setDoc(doc(db, 'obiettivi', values.id), {
                nome: values.nome,
                punti: Number(values.punti),
                attivo: true,
            });
            await queryClient.invalidateQueries({queryKey: ['obiettivi']});
            setAddOpen(false);
            form.resetFields();
            message.success('Obiettivo creato');
        } catch (e) {
            message.error('Errore nella creazione');
        }
    }

    return (
        <>
            <NavBar/>
            <main>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                    <h2 style={{margin: 0}}>Obiettivi</h2>
                    <Button
                        type="primary"
                        disabled={availableIds.length === 0}
                        onClick={() => setAddOpen(true)}
                    >
                        Aggiungi obiettivo
                    </Button>
                </div>

                <p style={{color: '#888', marginBottom: 20}}>
                    Gli ID 0 e 4 sono riservati (aggiunta punti / gift card) e non possono essere usati. Massimo 8 obiettivi.
                </p>

                {isLoading ? (
                    <div style={{textAlign: 'center', padding: 40}}><Spin/></div>
                ) : sorted.length === 0 ? (
                    <p style={{color: '#888'}}>Nessun obiettivo. Aggiungine uno per iniziare.</p>
                ) : (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16}}>
                        {sorted.map(o => (
                            <ObiettivoCard
                                key={o.id}
                                obiettivo={o}
                                onSave={handleSave}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}

                <Modal
                    title="Nuovo obiettivo"
                    open={addOpen}
                    onCancel={() => {
                        setAddOpen(false);
                        form.resetFields();
                    }}
                    footer={null}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical" onFinish={handleAdd}>
                        <Form.Item
                            label="ID (cifra)"
                            name="id"
                            rules={[{required: true, message: 'Seleziona un ID'}]}
                            help="Identifica il QR code generato dall'app cliente. Una volta scelto non si può cambiare."
                        >
                            <Select
                                placeholder="Scegli una cifra"
                                options={availableIds.map(d => ({value: d, label: d}))}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Nome"
                            name="nome"
                            rules={[{required: true, message: 'Inserisci un nome'}]}
                        >
                            <Input placeholder='es. Cena Gratis in Braseria'/>
                        </Form.Item>
                        <Form.Item
                            label="Punti richiesti"
                            name="punti"
                            rules={[
                                {required: true, message: 'Inserisci i punti'},
                                {
                                    validator: (_, v) => {
                                        const n = Number(v);
                                        if (!Number.isFinite(n) || n <= 0) return Promise.reject('Numero positivo richiesto');
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input type="number" placeholder="es. 600"/>
                        </Form.Item>
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
                            <Button onClick={() => {
                                setAddOpen(false);
                                form.resetFields();
                            }}>Annulla</Button>
                            <Button type="primary" htmlType="submit">Crea</Button>
                        </div>
                    </Form>
                </Modal>
            </main>
        </>
    );
}

function ObiettivoCard({obiettivo, onSave, onDelete}) {
    const [nome, setNome] = useState(obiettivo.nome ?? '');
    const [punti, setPunti] = useState(String(obiettivo.punti ?? ''));
    const [attivo, setAttivo] = useState(obiettivo.attivo ?? true);
    const [saving, setSaving] = useState(false);

    const dirty = nome !== (obiettivo.nome ?? '')
        || String(obiettivo.punti ?? '') !== punti
        || (obiettivo.attivo ?? true) !== attivo;

    async function save() {
        const puntiNum = Number(punti);
        if (!Number.isFinite(puntiNum) || puntiNum <= 0) {
            message.error('Punti non validi');
            return;
        }
        if (!nome.trim()) {
            message.error('Nome obbligatorio');
            return;
        }
        setSaving(true);
        await onSave(obiettivo.id, {nome: nome.trim(), punti: puntiNum, attivo});
        setSaving(false);
    }

    return (
        <Card
            title={<span>ID <b>{obiettivo.id}</b></span>}
            extra={
                <Popconfirm
                    title="Eliminare questo obiettivo?"
                    description="I QR generati da app vecchie con questo ID non funzioneranno più."
                    okText="Elimina"
                    okType="danger"
                    cancelText="Annulla"
                    onConfirm={() => onDelete(obiettivo.id)}
                >
                    <Button danger size="small">Elimina</Button>
                </Popconfirm>
            }
        >
            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                <div>
                    <label style={{fontSize: 12, color: '#888'}}>Nome</label>
                    <Input value={nome} onChange={e => setNome(e.target.value)}/>
                </div>
                <div>
                    <label style={{fontSize: 12, color: '#888'}}>Punti richiesti</label>
                    <Input type="number" value={punti} onChange={e => setPunti(e.target.value)}/>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <span style={{fontSize: 12, color: '#888'}}>Attivo</span>
                    <Switch checked={attivo} onChange={setAttivo}/>
                </div>
                <Button
                    type="primary"
                    disabled={!dirty || saving}
                    loading={saving}
                    onClick={save}
                >
                    Salva
                </Button>
            </div>
        </Card>
    );
}

export default ObiettiviPage;
